// calcAverages.js

const toNumber = (v) => {
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
};

const averagePositive = (values) => {
  const nums = values
    .map(toNumber)
    .filter((n) => n !== null && n > 0); // exclude 0 from divisor
  if (nums.length === 0) return 0;
  const sum = nums.reduce((a, b) => a + b, 0);
  return Math.round(sum / nums.length);
};

const ENV_KEYS = [
  'climate_score',
  'water_score',
  'biodiversity_score',
  'transport_score',
  'land_use_score',
  'fertilizers_use_score',
  'pesticides_use_score',
];

const SOCIAL_KEYS = [
  'social_justice_score',
  'child_labor_score',
  'equality_score',
  'local_population_score',
  'worker_rights_score',
  'animal_protection_score',
];

// Per-component averages (ingredient or dish-like object)
export const calculateSustainabilityAverages = (component) => {
  if (!component) return { environment_average: 0, health_average: 0, social_average: 0 };

  const envVals = ENV_KEYS.map((k) => component[k]);
  const socialVals = SOCIAL_KEYS.map((k) => component[k]);
  const healthVal = toNumber(component.health_score);

  return {
    environment_average: averagePositive(envVals),
    // Health average is based only on health_score (exclude 0 from divisor)
    health_average: healthVal && healthVal > 0 ? Math.round(healthVal) : 0,
    social_average: averagePositive(socialVals),
  };
};

// Dish-level weighted averages from ingredients
export const calculateDishSustainabilityAverages = (ingredients) => {
  if (!Array.isArray(ingredients) || ingredients.length === 0) {
    return { environment_average: 0, health_average: 0, social_average: 0 };
  }

  let envWeighted = 0;
  let envWeight = 0;
  let healthWeighted = 0;
  let healthWeight = 0;
  let socialWeighted = 0;
  let socialWeight = 0;

  for (const ing of ingredients) {
    const weight = Math.max(0, toNumber(ing.weight_in_percentage) ?? 0);
    if (weight <= 0) continue;

    // Environment
    const envAvg = averagePositive(ENV_KEYS.map((k) => ing[k]));
    if (envAvg > 0) {
      envWeighted += envAvg * weight;
      envWeight += weight;
    }

    // Health (only health_score)
    const h = toNumber(ing.health_score);
    if (h !== null && h > 0) {
      healthWeighted += h * weight;
      healthWeight += weight;
    }

    // Social
    const socialAvg = averagePositive(SOCIAL_KEYS.map((k) => ing[k]));
    if (socialAvg > 0) {
      socialWeighted += socialAvg * weight;
      socialWeight += weight;
    }
  }

  return {
    environment_average: envWeight > 0 ? Math.round(envWeighted / envWeight) : 0,
    health_average: healthWeight > 0 ? Math.round(healthWeighted / healthWeight) : 0,
    social_average: socialWeight > 0 ? Math.round(socialWeighted / socialWeight) : 0,
  };
};

// Prefer precalculated dish averages; else derive from ingredients; else from item scores
export const getDishAverages = (item) => {
  if (!item) return { environment_average: 0, health_average: 0, social_average: 0 };

  const hasAllPrecalc =
    typeof item.environment_average === 'number' &&
    typeof item.health_average === 'number' &&
    typeof item.social_average === 'number' &&
    !Number.isNaN(item.environment_average) &&
    !Number.isNaN(item.health_average) &&
    !Number.isNaN(item.social_average);

  if (hasAllPrecalc) {
    return {
      environment_average: item.environment_average,
      health_average: item.health_average,
      social_average: item.social_average,
    };
  }

  if (Array.isArray(item.ingredients) && item.ingredients.length > 0) {
    return calculateDishSustainabilityAverages(item.ingredients);
  }

  return calculateSustainabilityAverages(item);
};